import type { LocationQueryRaw, RouteLocationRaw } from "vue-router";

export type PublicationSummary = {
  id: string;
  number: number | null;
  kind?: string | null;
  title?: string;
  publishDate?: string | null;
};

export type MagazinePublication = { kind: "magazine"; id: string; number: number };
export type HandoutPublication = { kind: "handout"; id: string; number: null };
export type PublicationIdentity = MagazinePublication | HandoutPublication;

type Address = { name?: unknown; params: Record<string, unknown> };
type TargetOptions = { query?: LocationQueryRaw; hash?: string };

const magazineNames = new Set(["issue-home", "plan", "review", "review-version", "upload-report"]);
const handoutNames = new Set(["handout-home", "handout-review", "handout-review-version", "handout-upload-report"]);

function routeParam(params: Record<string, unknown>, key: string): string | null {
  const value = params[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function magazineNumber(value: string | null): number | null {
  if (!value || !/^(0|[1-9]\d*)$/.test(value)) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) ? number : null;
}

function publicationKind(publication: PublicationSummary): "magazine" | "handout" | null {
  if (publication.kind === "handout") return "handout";
  if (publication.kind === "magazine" || publication.kind === undefined || publication.kind === null) return "magazine";
  return null;
}

export function resolvePublication(address: Address, publications: PublicationSummary[]): PublicationIdentity | null {
  const name = typeof address.name === "string" ? address.name : "";
  if (magazineNames.has(name)) {
    const number = magazineNumber(routeParam(address.params, "number"));
    if (number === null) return null;
    const publication = publications.find((item) => publicationKind(item) === "magazine" && item.number === number);
    return publication ? { kind: "magazine", id: publication.id, number } : null;
  }
  if (handoutNames.has(name)) {
    const id = routeParam(address.params, "id");
    if (!id) return null;
    const publication = publications.find((item) => publicationKind(item) === "handout" && item.id === id);
    return publication ? { kind: "handout", id, number: null } : null;
  }
  return null;
}

export function publicationHomeTarget(workspaceId: string, publication: PublicationIdentity, options: TargetOptions = {}): RouteLocationRaw {
  return publication.kind === "magazine"
    ? { name: "issue-home", params: { ws: workspaceId, number: publication.number }, ...options }
    : { name: "handout-home", params: { ws: workspaceId, id: publication.id }, ...options };
}

export function publicationReviewTarget(workspaceId: string, publication: PublicationIdentity, options: TargetOptions = {}): RouteLocationRaw {
  return publication.kind === "magazine"
    ? { name: "review", params: { ws: workspaceId, number: publication.number }, ...options }
    : { name: "handout-review", params: { ws: workspaceId, id: publication.id }, ...options };
}

export function publicationReviewVersionTarget(workspaceId: string, publication: PublicationIdentity, version: number, options: TargetOptions = {}): RouteLocationRaw {
  return publication.kind === "magazine"
    ? { name: "review-version", params: { ws: workspaceId, number: publication.number, v: version }, ...options }
    : { name: "handout-review-version", params: { ws: workspaceId, id: publication.id, v: version }, ...options };
}

export function publicationReportTarget(workspaceId: string, publication: PublicationIdentity, version: number, options: TargetOptions = {}): RouteLocationRaw {
  return publication.kind === "magazine"
    ? { name: "upload-report", params: { ws: workspaceId, number: publication.number, v: version }, ...options }
    : { name: "handout-upload-report", params: { ws: workspaceId, id: publication.id, v: version }, ...options };
}

export function publicationReportPath(workspaceId: string, publication: PublicationIdentity, version: number, documentId: string): string {
  const base = publication.kind === "magazine"
    ? `/workspaces/${encodeURIComponent(workspaceId)}/inprint/issues/${publication.number}/versions/${version}/report`
    : `/workspaces/${encodeURIComponent(workspaceId)}/inprint/handouts/${encodeURIComponent(publication.id)}/versions/${version}/report`;
  return `${base}?document=${encodeURIComponent(documentId)}`;
}

export function publicationPlanTarget(workspaceId: string, publication: MagazinePublication, options: TargetOptions = {}): RouteLocationRaw {
  return { name: "plan", params: { ws: workspaceId, number: publication.number }, ...options };
}
